'use client';
import React, { useState } from 'react';
import { ArrowRight, RotateCcw, Save, Printer, CheckCircle, AlertTriangle, XCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { saveProject } from '@/lighting-app/data/projectStorage';
import type { InspectWizardState } from './InspectWizardClient';
import type { InspectionResult } from '@/lighting-app/data/lightingStandards';
import LuxGauge from './LuxGauge';

interface Props {
  state: InspectWizardState;
  result: InspectionResult;
  onBack: () => void;
  onReset: () => void;
}

export default function InspectResults({ state, result, onBack, onReset }: Props) {
  const [projectName, setProjectName] = useState(
    state.roomType ? `فحص إضاءة ${state.roomType.nameAr}` : 'فحص إضاءة'
  );
  const [saved, setSaved] = useState(false);

  const statusConfig = {
    good: {
      icon: CheckCircle,
      color: 'text-success',
      bg: 'bg-green-50 border-green-200',
      emoji: '🟢',
      textColor: 'text-green-800',
    },
    warning: {
      icon: AlertTriangle,
      color: 'text-warning',
      bg: 'bg-yellow-50 border-yellow-200',
      emoji: '🟡',
      textColor: 'text-yellow-800',
    },
    poor: {
      icon: XCircle,
      color: 'text-danger',
      bg: 'bg-red-50 border-red-200',
      emoji: '🔴',
      textColor: 'text-red-800',
    },
    excess: {
      icon: TrendingUp,
      color: 'text-blue-500',
      bg: 'bg-blue-50 border-blue-200',
      emoji: '🔵',
      textColor: 'text-blue-800',
    },
  };

  const sc = statusConfig[result.status];
  const StatusIcon = sc.icon;

  const handleSave = () => {
    if (!state.roomType) return;
    // Backend integration point: POST /api/projects
    saveProject({
      name: projectName,
      type: 'inspect',
      roomTypeId: state.roomType.id,
      roomTypeNameAr: state.roomType.nameAr,
      area: result.area,
      targetLux: result.targetLux,
      fixtureCount: parseInt(state.existingCount),
      totalWatts: parseInt(state.existingCount) * (parseFloat(state.wattsPerFixture) || 0),
      currentLux: result.currentLux,
      status: result.status === 'excess' ? 'warning' : result.status,
      data: { state, result },
    });
    setSaved(true);
    toast.success(`تم حفظ التقرير: ${projectName}`);
  };

  const area = parseFloat(state.length) * parseFloat(state.width);

  return (
    <div className="slide-up max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-4xl mb-3">🔍</div>
        <h2 className="text-3xl font-bold text-foreground mb-2">نتيجة فحص الإضاءة</h2>
        <p className="text-muted-foreground">
          تقرير تفصيلي لإضاءة {state.roomType?.nameAr} — {area.toFixed(1)} م²
        </p>
      </div>

      {/* Status Banner */}
      <div className={`rounded-2xl border-2 p-6 mb-8 ${sc.bg}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <StatusIcon size={44} className={sc.color} />
          <div className="flex-1">
            <div className="text-xl font-bold text-foreground mb-1">
              {sc.emoji} {result.statusAr}
            </div>
            <p className={`text-sm leading-relaxed ${sc.textColor}`}>
              {result.recommendation}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Gauge */}
        <div className="wizard-card p-6 flex flex-col items-center">
          <h3 className="text-lg font-bold text-foreground mb-4 w-full">مؤشر مستوى الإضاءة</h3>
          <LuxGauge
            currentLux={result.currentLux}
            targetLux={result.targetLux}
            minLux={result.minLux}
            maxLux={result.maxLux}
            percentage={result.luxPercentage}
            status={result.status}
          />
        </div>

        {/* Comparison Metrics */}
        <div className="wizard-card p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">مقارنة الإضاءة</h3>
          <div className="space-y-4">
            {/* Current Lux */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
              <div>
                <div className="text-xs text-muted-foreground font-semibold mb-0.5">الإضاءة الحالية</div>
                <div className={`text-2xl font-bold font-tabular ${sc.color}`}>
                  {Math.round(result.currentLux)} Lux
                </div>
              </div>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                result.status === 'good' ? 'bg-green-100' :
                result.status === 'poor' ? 'bg-red-100' :
                result.status === 'excess' ? 'bg-blue-100' : 'bg-yellow-100'
              }`}>
                <StatusIcon size={22} className={sc.color} />
              </div>
            </div>

            {/* Target Lux */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
              <div>
                <div className="text-xs text-muted-foreground font-semibold mb-0.5">الإضاءة الموصى بها</div>
                <div className="text-2xl font-bold font-tabular text-foreground">
                  {result.targetLux} Lux
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                <span className="text-primary text-xl">🎯</span>
              </div>
            </div>

            {/* Difference */}
            {result.status !== 'good' && (
              <div className="flex items-center justify-between p-4 rounded-xl bg-background border border-border fade-in">
                <div>
                  <div className="text-xs text-muted-foreground font-semibold mb-0.5">
                    {result.status === 'excess' ? 'الفائض' : 'النقص'}
                  </div>
                  <div className={`text-2xl font-bold font-tabular ${
                    result.status === 'excess' ? 'text-blue-500' : 'text-danger'
                  }`}>
                    {result.status === 'excess'
                      ? `+${Math.round(result.surplus)} Lux`
                      : `-${Math.round(result.deficit)} Lux`}
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  result.status === 'excess' ? 'bg-blue-100' : 'bg-red-100'
                }`}>
                  {result.status === 'excess'
                    ? <TrendingUp size={22} className="text-blue-500" />
                    : <TrendingDown size={22} className="text-danger" />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'عدد المصابيح الحالية', value: state.existingCount, unit: 'مصباح' },
          { label: 'Lumen لكل مصباح', value: Math.round(parseFloat(state.lumensPerFixture)).toLocaleString('ar'), unit: 'Lumen' },
          { label: 'إجمالي Lumen', value: Math.round(result.currentLumens).toLocaleString('ar'), unit: 'Lumen' },
          { label: 'مساحة المكان', value: area.toFixed(1), unit: 'm²' },
          ...(result.additionalFixturesNeeded > 0
            ? [{ label: 'مصابيح تحتاج إضافتها', value: String(result.additionalFixturesNeeded), unit: 'مصباح', highlight: true }]
            : []),
          ...(result.fixturesToRemove > 0
            ? [{ label: 'مصابيح يمكن إزالتها', value: String(result.fixturesToRemove), unit: 'مصباح', highlight: true }]
            : []),
        ].map((m, i) => (
          <div
            key={`inspect-metric-${i + 1}`}
            className={`result-metric-card ${(m as { highlight?: boolean }).highlight ? 'border-primary/30 bg-accent' : ''}`}
          >
            <div className="text-xs text-muted-foreground font-semibold mb-1">{m.label}</div>
            <div className={`text-2xl font-bold font-tabular ${(m as { highlight?: boolean }).highlight ? 'text-primary' : 'text-foreground'}`}>
              {m.value}
            </div>
            <div className="text-xs text-muted-foreground">{m.unit}</div>
          </div>
        ))}
      </div>

      {/* Lux Range Bar */}
      <div className="wizard-card p-6 mb-8">
        <h3 className="font-bold text-foreground mb-4">نطاق الإضاءة المقبول</h3>
        <div className="relative">
          {/* Bar background */}
          <div className="h-6 rounded-full overflow-hidden flex">
            <div className="bg-red-200 flex-none" style={{ width: '20%' }} />
            <div className="bg-yellow-200 flex-none" style={{ width: '15%' }} />
            <div className="bg-green-200 flex-1" />
            <div className="bg-yellow-200 flex-none" style={{ width: '15%' }} />
            <div className="bg-red-200 flex-none" style={{ width: '20%' }} />
          </div>

          {/* Current position indicator */}
          <div
            className="absolute top-0 w-1 h-6 bg-secondary rounded-full transition-all duration-700"
            style={{
              left: `${Math.min(Math.max((result.currentLux / (result.maxLux * 1.5)) * 100, 2), 98)}%`,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>ضعيف جداً</span>
          <span className="text-success font-semibold">مناسب ({result.minLux}–{result.maxLux} Lux)</span>
          <span>مرتفع جداً</span>
        </div>
      </div>

      {/* Save Project */}
      <div className="wizard-card p-6 mb-8">
        <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
          <Save size={18} className="text-secondary" />
          حفظ التقرير
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="اسم التقرير"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground focus:outline-none focus:border-secondary"
          />
          <button
            onClick={handleSave}
            disabled={saved}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              saved ? 'bg-success text-white cursor-not-allowed' : 'gradient-dark text-white hover:opacity-90'
            }`}
          >
            {saved ? '✓ محفوظ' : 'حفظ'}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 justify-between">
        <button onClick={onBack} className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
          <ArrowRight size={18} />
          <span>تعديل</span>
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="px-5 py-3 rounded-xl border-2 border-border text-secondary font-semibold flex items-center gap-2 hover:border-secondary hover:text-secondary transition-all"
          >
            <Printer size={18} />
            <span className="hidden sm:inline">طباعة</span>
          </button>
          <button
            onClick={onReset}
            className="px-6 py-3 rounded-xl font-bold text-white flex items-center gap-2 gradient-dark hover:opacity-90 transition-all"
          >
            <RotateCcw size={16} />
            <span>فحص جديد</span>
          </button>
        </div>
      </div>
    </div>
  );
}